import React, { forwardRef } from "react";
import { imageUrl, rupee } from "../lib/api";
import {
  formatInvoiceDate,
  gstSummaryLines,
  invoiceLineItems,
  paymentStatusLabel,
  storeBillingAddress,
} from "../lib/saleInvoice";

function MetaField({ label, value }) {
  if (value === undefined || value === null || value === "") return null;
  return (
    <tr>
      <td className="gi-info-label">{label}</td>
      <td className="gi-info-value">{value}</td>
    </tr>
  );
}

function InfoPairRow({ label1, value1, label2, value2 }) {
  const showLeft = value1 !== undefined && value1 !== null && value1 !== "";
  const showRight = value2 !== undefined && value2 !== null && value2 !== "";
  if (!showLeft && !showRight) return null;
  return (
    <tr>
      <td className="gi-info-label">{showLeft ? label1 : ""}</td>
      <td className="gi-info-value">{showLeft ? value1 : ""}</td>
      <td className="gi-info-label gi-info-split">{showRight ? label2 : ""}</td>
      <td className="gi-info-value">{showRight ? value2 : ""}</td>
    </tr>
  );
}

function PartyBlock({ snap, gstin }) {
  const name = snap?.name || "Walk-in";
  const addressLine = [snap?.address, snap?.city, snap?.state].filter(Boolean).join(", ");
  return (
    <table className="gi-info-table gi-info-party">
      <tbody>
        <MetaField label="Name" value={name} />
        {snap?.customer_name && snap.customer_name !== name && (
          <MetaField label="Customer" value={snap.customer_name} />
        )}
        <MetaField label="GSTIN" value={gstin ?? snap?.gstin} />
        <MetaField label="Mobile" value={snap?.phone} />
        <MetaField label="Address" value={addressLine || undefined} />
      </tbody>
    </table>
  );
}

function shippingSnapshot(customerSnapshot) {
  if (!customerSnapshot) return null;
  if (customerSnapshot.shipping) {
    return {
      name: customerSnapshot.shipping.name || customerSnapshot.name,
      phone: customerSnapshot.shipping.phone || customerSnapshot.phone,
      address: customerSnapshot.shipping.address,
      city: customerSnapshot.shipping.city,
      state: customerSnapshot.shipping.state,
    };
  }
  return customerSnapshot;
}

const TaxInvoiceView = forwardRef(function TaxInvoiceView({ sale, store }, ref) {
  const priceIncludesGst = store?.product_price_includes_gst !== false;
  const billing = storeBillingAddress(store);
  const lines = sale ? invoiceLineItems({ ...sale, store_state: store?.state }, priceIncludesGst) : [];
  const interState = (sale?.igst_amount || 0) > 0;
  const placeOfSupply = sale?.customer_snapshot?.state || store?.state || "—";
  const companyAddress = billing
    ? [billing.address, [billing.city, billing.state, billing.pincode].filter(Boolean).join(", ")].filter(Boolean).join(", ")
    : "";

  if (!sale) return null;

  return (
    <div className="gst-invoice print-invoice" id="tax-invoice" ref={ref}>
      <div className="flex items-center justify-between px-2 py-1 gi-border-b text-[11px]">
        <span>Page No. 1 of 1</span>
        <span className="font-bold text-sm tracking-wide">TAX INVOICE</span>
        <span>Original Copy</span>
      </div>

      <div className="flex gi-border-b">
        <div className="w-24 shrink-0 gi-border-r p-2 flex items-center justify-center">
          {store?.image ? (
            <img src={imageUrl(store.image)} alt="" className="w-16 h-16 object-contain" />
          ) : (
            <div className="w-16 h-16 border border-dashed border-gray-400 grid place-items-center text-[10px] text-gray-500 text-center px-1">
              Logo
            </div>
          )}
        </div>
        <div className="flex-1 p-3 text-center min-w-0">
          <div className="gi-company-name">{store?.name || "Store"}</div>
          {companyAddress && <div className="mt-1">{companyAddress}</div>}
          <div className="mt-1 flex flex-wrap justify-center gap-x-4 gap-y-0.5">
            {store?.phone && <span>Mobile : {store.phone}</span>}
            {store?.email && <span>Email : {store.email}</span>}
          </div>
          {store?.gst_number && <div className="mt-0.5">GSTIN : {store.gst_number}</div>}
        </div>
      </div>

      <table className="gi-info-table gi-border-b">
        <colgroup>
          <col className="gi-col-label" />
          <col className="gi-col-value" />
          <col className="gi-col-label" />
          <col className="gi-col-value" />
        </colgroup>
        <tbody>
          <InfoPairRow
            label1="Invoice Number"
            value1={sale.invoice_no}
            label2="Supply Type"
            value2={interState ? "Inter-state" : "Intra-state"}
          />
          <InfoPairRow
            label1="Invoice Date"
            value1={formatInvoiceDate(sale.created_at)}
            label2="GST Type"
            value2={interState ? "IGST" : "CGST + SGST"}
          />
          <InfoPairRow
            label1="Place of Supply"
            value1={placeOfSupply}
            label2="Cashier"
            value2={sale.cashier_name || undefined}
          />
          <InfoPairRow
            label1="Reverse Charge"
            value1="N"
            label2="Payment Mode"
            value2={sale.payment_method ? String(sale.payment_method).toUpperCase() : undefined}
          />
          <tr>
            <td className="gi-info-label">Payment Status</td>
            <td className="gi-info-value">{paymentStatusLabel(sale.payment_status)}</td>
            <td className="gi-info-split" />
            <td />
          </tr>
        </tbody>
      </table>

      <div className="gi-split-2 gi-border-b">
        <div>
          <div className="gi-party-title">Billing Details</div>
          <PartyBlock snap={sale.customer_snapshot} />
        </div>
        <div>
          <div className="gi-party-title">Shipping Details</div>
          <PartyBlock snap={shippingSnapshot(sale.customer_snapshot)} gstin={sale.customer_snapshot?.gstin} />
        </div>
      </div>

      <div className="gi-scroll">
        <table className="gi-items-table">
          <thead>
            <tr>
              <th className="gi-th-center" style={{ width: "2rem" }}>Sr.</th>
              <th className="gi-th-left">Item Description</th>
              <th className="gi-th-center" style={{ width: "4.5rem" }}>HSN/SAC</th>
              <th className="gi-th-center" style={{ width: "2.5rem" }}>Qty</th>
              <th className="gi-th-center" style={{ width: "2.5rem" }}>Unit</th>
              <th className="gi-th-num" style={{ width: "4.5rem" }}>Price</th>
              <th className="gi-th-center" style={{ width: "3rem" }}>Tax %</th>
              <th className="gi-th-num" style={{ width: "5rem" }}>Amount (₹)</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((line) => (
              <tr key={line.sr}>
                <td className="gi-center">{line.sr}</td>
                <td>
                  <div>{line.name}</div>
                  {line.barcode && <div className="text-[10px] text-gray-600 font-mono">{line.barcode}</div>}
                </td>
                <td className="gi-center font-mono">{line.hsn_code || "—"}</td>
                <td className="gi-center">{line.qty}</td>
                <td className="gi-center">Pcs</td>
                <td className="gi-num">{rupee(line.unitPriceBeforeTax)}</td>
                <td className="gi-center">{line.rate}%</td>
                <td className="gi-num font-semibold">{rupee(line.lineTotal)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="gi-split-2 gi-border-t">
        <div className="gi-notes-panel min-h-[5rem]">
          {sale.notes?.trim() ? (
            <>
              <div className="font-semibold mb-1">Notes</div>
              <p className="whitespace-pre-wrap">{sale.notes.trim()}</p>
            </>
          ) : (
            <div className="text-gray-500">Notes</div>
          )}
          {sale.amount_paid > 0 && (
            <div className="mt-2 text-[11px]">
              Amount paid: {rupee(sale.amount_paid)}
              {sale.balance_due > 0 && <> · Balance: {rupee(sale.balance_due)}</>}
            </div>
          )}
        </div>
        <div className="gi-summary">
          <div className="gi-summary-row">
            <span className="gi-summary-label">Taxable Amount</span>
            <span className="gi-summary-value">{rupee(sale.subtotal)}</span>
          </div>
          {gstSummaryLines(sale).map((line) => (
            <div key={line.label} className="gi-summary-row">
              <span className="gi-summary-label">{line.label}</span>
              <span className="gi-summary-value">{rupee(line.amount)}</span>
            </div>
          ))}
          {gstSummaryLines(sale).length > 1 && (
            <div className="gi-summary-row">
              <span className="gi-summary-label">Total GST</span>
              <span className="gi-summary-value">{rupee(sale.tax_amount)}</span>
            </div>
          )}
          <div className="gi-summary-row">
            <span className="gi-summary-label">Discount</span>
            <span className="gi-summary-value">{sale.discount > 0 ? `-${rupee(sale.discount)}` : rupee(0)}</span>
          </div>
          <div className="gi-summary-row gi-summary-total">
            <span className="gi-summary-label">Total</span>
            <span className="gi-summary-value">{rupee(sale.total)}</span>
          </div>
        </div>
      </div>
    </div>
  );
});

export default TaxInvoiceView;
